import React, { useState, useEffect, useContext } from 'react';
import './TitleBar.css';
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useNavigate } from 'react-router-dom';
import Route_Logo from '../../images/logo_route.svg';
import { useUser } from "../../pages/login/UserContextPersistent";
import Pro_icon from '../../images/navbar/profile/profile_icon.svg';
import Org_icon from '../../images/navbar/profile/org_icon.svg';
import Log_icon from '../../images/navbar/profile/logout_icon.svg';
import { LanguageContext } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import LocalizationModal from '../common/LocalizationModal';

export default function TitleBar() {
  const navigate = useNavigate();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isLangDropdownVisible, setIsLangDropdownVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [iconSize, setIconSize] = useState(window.innerWidth > 768 ? 40 : 30);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { state } = useUser();
  const { language, changeLanguage } = useContext(LanguageContext);
  const { t } = useTranslation();

  // Language options
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'kn', name: 'ಕನ್ನಡ', flag: '🇮🇳' },
    { code: 'ud', name: 'User Defined', flag: '🔄' }
  ];


  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
    if (isLangDropdownVisible) setIsLangDropdownVisible(false);
  };

  const toggleLangDropdown = () => {
    setIsLangDropdownVisible(!isLangDropdownVisible);
    if (isPopupVisible) setIsPopupVisible(false);
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsLangDropdownVisible(false);
  };

  const openLocalizationModal = () => {
    setIsLangDropdownVisible(false);
    setIsModalOpen(true);
  };

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      setIconSize(newIsMobile ? 30 : 40);

      // Close popups on mobile screen resize
      if (newIsMobile) {
        setIsPopupVisible(false);
        setIsLangDropdownVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleNavigateUser = () => {
    const id = state?.user?.id;
    navigate("/user/AddUser", { state: { id } });
  };

  const handleNavigateOrg = () => {
    const id = state?.user?.id;
    navigate("/organization", { state: { id } });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPopupVisible && !event.target.closest('.dh-profile')) {
        setIsPopupVisible(false);
      }
      if (isLangDropdownVisible && !event.target.closest('.language-selector')) {
        setIsLangDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupVisible, isLangDropdownVisible]);

  // Get current language display name
  const getCurrentLanguage = () => {
    const lang = languages.find(l => l.code === language);
    return lang ? lang : languages[0]; // Default to English if not found
  };

  return (
    <div className={`title_container ${isMobile ? 'mobile' : ''}`}>
      <div className='dh-main'>
        <img src={Route_Logo} alt='Route Logo' className='logo' />
        <span className='dh-title'>{t('routeEye')}</span>
      </div>
      <div className="dh-actions">
        {/* Language Selector */}
        <div className="language-selector">
          <div className="language-button" onClick={toggleLangDropdown}>
            <LanguageIcon
              style={{
                fontSize: iconSize * 0.8,
                color: "rgba(24, 48, 89, 1)",
                cursor: "pointer",
                marginRight: "5px"
              }}
            />
            <span className="current-language">{getCurrentLanguage().flag}</span>
            <svg
              width="12"
              height="6"
              viewBox="0 0 18 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginLeft: "5px" }}
            >
              <path
                d="M1 1L9 9L17 1"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {isLangDropdownVisible && (
            <div className="language-dropdown"
              style={{
                position: 'fixed',
                zIndex: 1000
              }}>
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  className={`language-item ${language === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className="language-flag">{lang.flag}</span>
                  <span className="language-name">{lang.name}</span>
                </div>
              ))}
              <div className="language-divider"></div>
              <div
                className="language-item upload-item"
                onClick={openLocalizationModal}
              >
                <span className="language-flag">
                  <FileUploadIcon fontSize="small" />
                </span>
                <span className="language-name">Upload Translations</span>
              </div>
            </div>
          )}
        </div>

        <div className="dh-profile">
          {/* Person Icon */}
          <div className="dh-image" onClick={togglePopup}>
            <PersonIcon
              style={{
                fontSize: iconSize,
                color: "rgba(24, 48, 89, 1)",
                background: "rgba(232, 234, 238, 1)",
                borderRadius: "50%",
                cursor: "pointer",
              }}
            />
          </div>

          {/* Popup */}
          {isPopupVisible && (
            <div className="profile-popup">
              <div className="popup-header">
                <PersonIcon
                  style={{
                    fontSize: "48px",
                    color: "rgba(24, 48, 89, 1)",
                    background: "rgba(232, 234, 238, 1)",
                    borderRadius: "50%",
                    padding: "8px",
                  }}
                />
                <div className="profile-info">
                  <div className="profile-name">{state?.user?.name || t('userName')}</div>
                  <div className="profile-username">{state?.user?.role || t('userRole')}</div>
                </div>
              </div>
              <div className="popup-menu">
                <div className="menu-item" onClick={handleNavigateUser}>
                  <img src={Pro_icon} alt="Profile" /> {t('myProfile')}
                </div>
                <div className="menu-item" onClick={handleNavigateOrg}>
                  <img src={Org_icon} alt="Organization" /> {t('myOrganization')}
                </div>
                <div className="menu-item" onClick={handleLogout}>
                  <img src={Log_icon} alt="Logout" /> {t('logout')}
                </div>
              </div>
            </div>
          )}

          <div style={{ cursor: "pointer" }} onClick={togglePopup}>
            <svg
              width="18"
              height="10"
              viewBox="0 0 18 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L9 9L17 1"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Localization Modal */}
      <LocalizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}